import { type KeyboardEvent, useEffect, useRef, useState } from "react";

interface CredentialDialogProps {
  credential: string;
  onClose: () => void;
}

export function CredentialDialog({ credential, onClose }: CredentialDialogProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const copyRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previous?.focus();
  }, []);

  const trapFocus = (event: KeyboardEvent) => {
    if (event.key !== "Tab") return;
    if (!event.shiftKey && document.activeElement === closeRef.current) {
      event.preventDefault();
      copyRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === copyRef.current) {
      event.preventDefault();
      closeRef.current?.focus();
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(credential);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="credential-dialog" role="dialog" aria-modal="true" aria-labelledby="credential-title" onKeyDown={trapFocus}>
        <span className="credential-badge" aria-hidden="true">!</span>
        <p className="eyebrow">面板憑證</p>
        <h2 id="credential-title">只顯示這一次</h2>
        <p>請現在複製到面板的本機設定。關閉後無法再次查看，只能撤銷並重建。</p>
        <code className="credential-value">{credential}</code>
        <button ref={copyRef} type="button" className="button button-secondary button-wide" onClick={() => void copy()}>
          {copyState === "copied" ? "已複製" : "複製憑證"}
        </button>
        {copyState === "failed" ? <p className="notice notice-error" role="alert">無法自動複製，請長按上方文字手動複製。</p> : null}
        <button ref={closeRef} type="button" className="button button-primary button-wide" onClick={onClose}>我已安全保存</button>
      </section>
    </div>
  );
}
